import PasswordGenerator from "@rabbit-company/password-generator";
import cytoscape from "cytoscape";
import dagre from "cytoscape-dagre";
import TOML from "smol-toml";

cytoscape.use(dagre);

let config = {
	database: { url: "" },
	clickhouse: { url: "" },
	server: {},
	adminAPI: {},
	logger: {},
	missingPulseDetector: {},
	selfMonitoring: {},
	monitors: [],
	groups: [],
	status_pages: [],
	notifications: { channels: {} },
	PulseMonitors: [],
};

function uid() {
	return crypto.randomUUID();
}

function token() {
	return PasswordGenerator.generate(50, true, true, false);
}

function esc(str) {
	if (typeof str !== "string") return str;
	return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function updateCardTitle(input) {
	const card = input.closest(".config-card");
	if (card) {
		const h3 = card.querySelector(".config-card-title h3");
		if (h3) h3.textContent = input.value || "Unnamed";
	}
}

function resolvePath(path) {
	const parts = path.split(".");
	let cur = config;
	for (let i = 0; i < parts.length - 1; i++) {
		const k = parts[i];
		if (cur[k] === undefined) cur[k] = {};
		cur = cur[k];
	}
	return { parent: cur, key: parts[parts.length - 1] };
}

function ensurePath(path) {
	const parts = path.split(".");
	let cur = config;
	for (const k of parts) {
		if (cur[k] === undefined) cur[k] = {};
		cur = cur[k];
	}
}

function showToast(msg, error = false) {
	const t = document.getElementById("toast");
	t.textContent = msg;
	t.className = "toast" + (error ? " error" : "");
	requestAnimationFrame(() => t.classList.add("show"));
	setTimeout(() => t.classList.remove("show"), 5000);
}

function flushActiveTabEdits() {
	const activeTab = document.querySelector("#tabs .tab.active")?.dataset?.tab;
	switch (activeTab) {
		case "general":
			readGeneralFromUI();
			break;
		case "monitors":
			flushBindings(document.getElementById("monitors-list"));
			break;
		case "groups":
			flushBindings(document.getElementById("groups-list"));
			break;
		case "statuspages":
			flushBindings(document.getElementById("statuspages-list"));
			break;
		case "notifications":
			flushBindings(document.getElementById("notifications-list"));
			break;
		case "pulsemonitors":
			flushBindings(document.getElementById("pulsemonitors-list"));
			break;
		default:
			break;
	}
}

function pruneNotificationChannelRefs(...ids) {
	const removeSet = new Set(ids.filter(Boolean));
	if (removeSet.size === 0) return;
	// Monitors
	for (const m of config.monitors || []) {
		if (Array.isArray(m.notificationChannels)) {
			m.notificationChannels = m.notificationChannels.filter((v) => !removeSet.has(v));
			if (m.notificationChannels.length === 0) delete m.notificationChannels;
		}
	}
	// Groups
	for (const g of config.groups || []) {
		if (Array.isArray(g.notificationChannels)) {
			g.notificationChannels = g.notificationChannels.filter((v) => !removeSet.has(v));
			if (g.notificationChannels.length === 0) delete g.notificationChannels;
		}
	}
}

function prunePulseMonitorRefs(...ids) {
	const removeSet = new Set(ids.filter(Boolean));
	if (removeSet.size === 0) return;

	// Monitors
	for (const m of config.monitors || []) {
		if (Array.isArray(m.pulseMonitors)) {
			m.pulseMonitors = m.pulseMonitors.filter((v) => !removeSet.has(v));
			if (m.pulseMonitors.length === 0) delete m.pulseMonitors;
		}
	}

	// Groups (in case the schema supports it now or later)
	for (const g of config.groups || []) {
		if (Array.isArray(g.pulseMonitors)) {
			g.pulseMonitors = g.pulseMonitors.filter((v) => !removeSet.has(v));
			if (g.pulseMonitors.length === 0) delete g.pulseMonitors;
		}
	}
}

function pruneDependencyRefs(...ids) {
	const removeSet = new Set(ids.filter(Boolean));
	if (removeSet.size === 0) return;

	// Monitors
	for (const m of config.monitors || []) {
		if (Array.isArray(m.dependencies)) {
			m.dependencies = m.dependencies.filter((v) => !removeSet.has(v));
			if (m.dependencies.length === 0) delete m.dependencies;
		}
	}

	// Groups
	for (const g of config.groups || []) {
		if (Array.isArray(g.dependencies)) {
			g.dependencies = g.dependencies.filter((v) => !removeSet.has(v));
			if (g.dependencies.length === 0) delete g.dependencies;
		}
	}
}

function pruneChildrenRefs(...ids) {
	const removeSet = new Set(ids.filter(Boolean));
	if (removeSet.size === 0) return;

	// Monitors
	for (const m of config.monitors || []) {
		if (Array.isArray(m.children)) {
			m.children = m.children.filter((v) => !removeSet.has(v));
			if (m.children.length === 0) delete m.children;
		}
	}

	// Groups
	for (const g of config.groups || []) {
		if (Array.isArray(g.children)) {
			g.children = g.children.filter((v) => !removeSet.has(v));
			if (g.children.length === 0) delete g.children;
		}
	}
}

function pruneStatusPageItemRefs(...ids) {
	const removeSet = new Set(ids.filter(Boolean));
	if (removeSet.size === 0) return;

	for (const sp of config.status_pages || []) {
		if (Array.isArray(sp.items)) {
			sp.items = sp.items.filter((v) => !removeSet.has(v));
		}
		if (Array.isArray(sp.leafItems)) {
			sp.leafItems = sp.leafItems.filter((v) => !removeSet.has(v));
			if (sp.leafItems.length === 0) delete sp.leafItems;
		}
	}
}

function replaceNotificationChannelRefs(oldId, newId) {
	if (!oldId || !newId || oldId === newId) return;
	const replace = (arr) => arr.map((v) => (v === oldId ? newId : v));
	for (const m of config.monitors || []) {
		if (Array.isArray(m.notificationChannels)) m.notificationChannels = replace(m.notificationChannels);
	}
	for (const g of config.groups || []) {
		if (Array.isArray(g.notificationChannels)) g.notificationChannels = replace(g.notificationChannels);
	}
}

document.getElementById("tabs").addEventListener("click", (e) => {
	const tab = e.target.closest(".tab");
	if (!tab) return;
	// Capture which tab we are leaving (used for dependent refreshes)
	const prevTab = document.querySelector("#tabs .tab.active")?.dataset?.tab;
	// Ensure we don't lose edits (and keep dependent UIs in sync)
	flushActiveTabEdits();

	document.querySelectorAll(".tab").forEach((t) => t.classList.remove("active"));
	document.querySelectorAll(".tab-panel").forEach((p) => p.classList.remove("active"));
	tab.classList.add("active");
	document.getElementById("panel-" + tab.dataset.tab).classList.add("active");

	renderMonitors();
	renderGroups();
	renderStatusPages();
	renderNotifications();
	renderPulseMonitors();
});

window.addEventListener("scroll", () => {
	document.getElementById("header").classList.toggle("scrolled", window.pageYOffset > 50);
});

document.getElementById("mobileMenuBtn")?.addEventListener("click", () => {
	document.getElementById("mobileMenu").classList.toggle("active");
});

document.getElementById("fileInput").addEventListener("change", async (e) => {
	const file = e.target.files[0];
	if (!file) return;
	try {
		const text = await file.text();
		parseTOML(text);
		showToast("Configuration imported successfully!");
	} catch (err) {
		showToast("Failed to parse TOML: " + err.message, true);
	}
	e.target.value = "";
});

// Toggle Loki settings visibility
document.getElementById("loki-enabled").addEventListener("change", (e) => {
	const lokiSettings = document.getElementById("loki-settings");
	lokiSettings.style.display = e.target.checked ? "block" : "none";
	// Pre-populate default labels when first enabled
	if (e.target.checked && document.querySelectorAll("[data-loki-label-key]").length === 0) {
		renderLokiLabels({ app: "uptime-monitor", env: "production" });
	}
});

function parseTOML(text) {
	config = TOML.parse(text);
	if (!config.monitors) config.monitors = [];
	if (!config.groups) config.groups = [];
	if (!config.status_pages) config.status_pages = [];
	if (!config.notifications) config.notifications = { channels: {} };
	if (!config.notifications.channels) config.notifications.channels = {};
	if (!config.PulseMonitors) config.PulseMonitors = [];
	if (!config.adminAPI) config.adminAPI = {};
	loadConfigToUI();
}

function loadConfigToUI() {
	document.getElementById("db-url").value = config.database?.url || "";
	document.getElementById("ch-url").value = config.clickhouse?.url || "";
	document.getElementById("srv-port").value = config.server?.port || "";
	document.getElementById("srv-proxy").value = config.server?.proxy || "";
	document.getElementById("srv-reloadToken").value = config.server?.reloadToken || "";
	document.getElementById("srv-burrowgate-originSecret").value = config.server?.burrowgate?.originSecret || "";
	document.getElementById("srv-burrowgate-maxAgeSeconds").value = config.server?.burrowgate?.maxAgeSeconds ?? "";
	document.getElementById("log-level").value = config.logger?.level ?? "3";

	// Loki transport
	const lokiEnabled = !!config.logger?.loki;
	document.getElementById("loki-enabled").checked = lokiEnabled;
	document.getElementById("loki-settings").style.display = lokiEnabled ? "block" : "none";
	document.getElementById("loki-url").value = config.logger?.loki?.url || "http://loki:3100";
	document.getElementById("loki-tenantId").value = config.logger?.loki?.tenantID || "";
	document.getElementById("loki-batchSize").value = config.logger?.loki?.batchSize || "";
	document.getElementById("loki-batchTimeout").value = config.logger?.loki?.batchTimeout || "";
	document.getElementById("loki-maxQueueSize").value = config.logger?.loki?.maxQueueSize || "";
	document.getElementById("loki-auth-username").value = config.logger?.loki?.basicAuth?.username || "";
	document.getElementById("loki-auth-password").value = config.logger?.loki?.basicAuth?.password || "";
	renderLokiLabels(config.logger?.loki?.labels || { app: "uptime-monitor", env: "production" });

	document.getElementById("mpd-interval").value = config.missingPulseDetector?.interval || "";
	document.getElementById("sm-enabled").checked = config.selfMonitoring?.enabled || false;
	document.getElementById("sm-id").value = config.selfMonitoring?.id || "";
	document.getElementById("sm-interval").value = config.selfMonitoring?.interval || "";
	document.getElementById("sm-backfill").checked = config.selfMonitoring?.backfillOnRecovery || false;
	document.getElementById("sm-latencyStrategy").value = config.selfMonitoring?.latencyStrategy || "last-known";

	document.getElementById("admin-enabled").checked = config.adminAPI?.enabled || false;
	document.getElementById("admin-token").value = config.adminAPI?.token || "";

	renderMonitors();
	renderGroups();
	renderStatusPages();
	renderNotifications();
	renderPulseMonitors();
	updateBadges();
}

function updateBadges() {
	document.getElementById("badge-monitors").textContent = config.monitors.length;
	document.getElementById("badge-groups").textContent = config.groups.length;
	document.getElementById("badge-statuspages").textContent = config.status_pages.length;
	document.getElementById("badge-notifications").textContent = Object.keys(config.notifications?.channels || {}).length;
	document.getElementById("badge-pulsemonitors").textContent = config.PulseMonitors.length;
	renderGraph();
}

function readGeneralFromUI() {
	const dbUrl = document.getElementById("db-url").value.trim();
	config.database = {
		url: dbUrl || "sqlite://./databases/uptime_monitor.db",
	};

	const url = document.getElementById("ch-url").value.trim();
	config.clickhouse = {
		url: url || "http://uptime_user:uptime_password@clickhouse:8123/uptime_monitor",
	};

	const port = document.getElementById("srv-port").value;
	const proxy = document.getElementById("srv-proxy").value;
	const reloadToken = document.getElementById("srv-reloadToken").value.trim();
	config.server = {
		port: Number(port) || 3000,
		proxy: proxy || "direct",
		reloadToken: reloadToken || token(),
	};

	const burrowgateSecret = document.getElementById("srv-burrowgate-originSecret").value.trim();
	const burrowgateMaxAge = document.getElementById("srv-burrowgate-maxAgeSeconds").value;
	if (burrowgateSecret) {
		config.server.burrowgate = { originSecret: burrowgateSecret };
		if (burrowgateMaxAge !== "") config.server.burrowgate.maxAgeSeconds = Number(burrowgateMaxAge);
	}

	const logLevel = document.getElementById("log-level").value;
	config.logger = {
		level: logLevel === "" ? 3 : Number(logLevel),
	};

	// Loki transport
	if (document.getElementById("loki-enabled").checked) {
		const lokiUrl = document.getElementById("loki-url").value.trim();
		const lokiTenantId = document.getElementById("loki-tenantId").value.trim();
		const lokiBatchSize = document.getElementById("loki-batchSize").value;
		const lokiBatchTimeout = document.getElementById("loki-batchTimeout").value;
		const lokiMaxQueueSize = document.getElementById("loki-maxQueueSize").value;
		const lokiAuthUser = document.getElementById("loki-auth-username").value.trim();
		const lokiAuthPass = document.getElementById("loki-auth-password").value.trim();

		config.logger.loki = {};
		if (lokiUrl) config.logger.loki.url = lokiUrl;
		if (lokiTenantId) config.logger.loki.tenantID = lokiTenantId;
		if (lokiBatchSize) config.logger.loki.batchSize = Number(lokiBatchSize);
		if (lokiBatchTimeout) config.logger.loki.batchTimeout = Number(lokiBatchTimeout);
		if (lokiMaxQueueSize) config.logger.loki.maxQueueSize = Number(lokiMaxQueueSize);

		// Labels
		const labels = readLokiLabelsFromUI();
		if (Object.keys(labels).length > 0) config.logger.loki.labels = labels;

		// Basic Auth
		if (lokiAuthUser || lokiAuthPass) {
			config.logger.loki.basicAuth = {};
			if (lokiAuthUser) config.logger.loki.basicAuth.username = lokiAuthUser;
			if (lokiAuthPass) config.logger.loki.basicAuth.password = lokiAuthPass;
		}
	}

	const mpdInterval = document.getElementById("mpd-interval").value;
	config.missingPulseDetector = {
		interval: Number(mpdInterval) || 5,
	};

	const smEnabled = document.getElementById("sm-enabled").checked;
	const smId = document.getElementById("sm-id").value.trim();
	const smInterval = document.getElementById("sm-interval").value;
	const smBackfill = document.getElementById("sm-backfill").checked;
	const smStrategy = document.getElementById("sm-latencyStrategy").value;
	config.selfMonitoring = {
		enabled: smEnabled,
		id: smId || "self-monitor",
		interval: Number(smInterval) || 3,
		backfillOnRecovery: smBackfill,
		latencyStrategy: smStrategy || "last-known",
	};

	const adminEnabled = document.getElementById("admin-enabled").checked;
	const adminToken = document.getElementById("admin-token").value.trim();
	config.adminAPI = {
		enabled: adminEnabled,
		token: adminToken || token(),
	};
}

function renderLokiLabels(labels) {
	const container = document.getElementById("loki-labels-list");
	const entries = Object.entries(labels);
	if (entries.length === 0) {
		container.innerHTML =
			'<p style="color: var(--text-subtle); font-size: 0.85rem; margin-bottom: 8px;">No labels configured. Click "Add Label" to add one.</p>';
		return;
	}
	container.innerHTML = entries
		.map(
			([key, value], i) => `
		<div class="form-grid" style="margin-bottom: 8px;">
			<div class="form-group">
				<label class="form-label">Key</label>
				<input class="form-input mono" type="text" value="${esc(key)}" data-loki-label-key="${i}" />
			</div>
			<div class="form-group">
				<label class="form-label">Value</label>
				<input class="form-input mono" type="text" value="${esc(String(value))}" data-loki-label-value="${i}" />
			</div>
			<div class="form-group" style="justify-content: flex-end;">
				<button class="btn-icon danger" type="button" data-action="remove-loki-label" data-idx="${i}" title="Remove label>
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
				</button>
			</div>
		</div>`,
		)
		.join("");
}

function renderLokiLabels(labels) {
	const container = document.getElementById("loki-labels-list");
	const entries = Object.entries(labels);
	if (entries.length === 0) {
		container.innerHTML =
			'<p style="color: var(--text-subtle); font-size: 0.85rem; margin-bottom: 8px;">No labels configured. Click "Add Label" to add one.</p>';
		return;
	}
	container.innerHTML = entries
		.map(
			([key, value], i) => `
		<div class="form-grid" style="margin-bottom: 8px;">
			<div class="form-group">
				<label class="form-label">Key</label>
				<input class="form-input mono" type="text" value="${esc(key)}" data-loki-label-key="${i}" />
			</div>
			<div class="form-group">
				<label class="form-label">Value</label>
				<input class="form-input mono" type="text" value="${esc(String(value))}" data-loki-label-value="${i}" />
			</div>
			<div class="form-group" style="justify-content: flex-end;">
				<button class="btn-icon danger" type="button" data-action="remove-loki-label" data-idx="${i}" title="Remove label">
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
				</button>
			</div>
		</div>`,
		)
		.join("");
}

function readLokiLabelsFromUI() {
	const labels = {};
	const keys = document.querySelectorAll("[data-loki-label-key]");
	const values = document.querySelectorAll("[data-loki-label-value]");
	keys.forEach((keyEl, i) => {
		const k = keyEl.value.trim();
		const v = values[i]?.value.trim() || "";
		if (k) labels[k] = v;
	});
	return labels;
}

// Add Label button
document.getElementById("btn-add-loki-label").addEventListener("click", () => {
	const current = readLokiLabelsFromUI();
	current[""] = "";
	renderLokiLabels(current);
	const allKeys = document.querySelectorAll("[data-loki-label-key]");
	allKeys[allKeys.length - 1]?.focus();
});

// Remove Label buttons (delegated)
document.getElementById("loki-labels-list").addEventListener("click", (e) => {
	const btn = e.target.closest('[data-action="remove-loki-label"]');
	if (!btn) return;
	const idx = Number(btn.dataset.idx);
	const current = readLokiLabelsFromUI();
	const entries = Object.entries(current);
	entries.splice(idx, 1);
	renderLokiLabels(Object.fromEntries(entries));
});

function addMonitor() {
	// Persist any unsaved edits in the current list before mutating
	flushBindings(document.getElementById("monitors-list"));
	config.monitors.push({
		id: uid(),
		name: "",
		token: token(),
		interval: 30,
		maxRetries: 0,
		resendNotification: 0,
	});
	renderMonitors();
	updateBadges();
}

function removeMonitor(idx) {
	flushBindings(document.getElementById("monitors-list"));
	const removed = config.monitors[idx];
	const removedId = removed?.id;
	config.monitors.splice(idx, 1);
	pruneDependencyRefs(removedId);
	pruneChildrenRefs(removedId);
	pruneStatusPageItemRefs(removedId);
	renderMonitors();
	renderGroups();
	renderStatusPages();
	updateBadges();
}

let isFlushing = false;

function flushBindings(container) {
	if (isFlushing) return;
	isFlushing = true;
	container.querySelectorAll("[data-bind]").forEach((el) => {
		const path = el.dataset.bind;
		if (!path) return;

		if (el.dataset.ensure) {
			ensurePath(el.dataset.ensure);
		}

		const { parent, key } = resolvePath(path);
		const dataType = el.dataset.type;
		const emptyUndefined = el.hasAttribute("data-empty-undefined");

		let value;
		if (dataType === "boolean") {
			value = el.checked;
		} else if (dataType === "number") {
			value = el.value === "" ? undefined : Number(el.value);
		} else if (dataType === "csv") {
			value = el.value
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);
		} else {
			value = el.value;
		}

		if (emptyUndefined && (value === "" || value === undefined)) {
			delete parent[key];
		} else {
			parent[key] = value;
		}
	});
	// Flush webhook headers textareas
	container.querySelectorAll('[data-action="webhook-headers"]').forEach((el) => {
		const key = el.dataset.key;
		ensurePath(`notifications.channels.${key}.webhook`);
		const headers = {};
		el.value
			.split("\n")
			.map((l) => l.trim())
			.filter(Boolean)
			.forEach((line) => {
				const sep = line.indexOf(":");
				if (sep > 0) {
					headers[line.slice(0, sep).trim()] = line.slice(sep + 1).trim();
				}
			});
		if (Object.keys(headers).length > 0) {
			config.notifications.channels[key].webhook.headers = headers;
		} else {
			delete config.notifications.channels[key].webhook.headers;
		}
	});

	// Flush SNMP OIDs
	container.querySelectorAll('[data-action="snmp-oid-key"]').forEach((el) => {
		flushSnmpOids(Number(el.dataset.idx));
	});

	isFlushing = false;
}

function renderMonitors() {
	const container = document.getElementById("monitors-list");

	if (config.monitors.length === 0) {
		container.innerHTML = `
<div class="empty-state">
<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
<h3>No Monitors</h3>
<p>Add monitors to track your services.</p>
</div>`;
		return;
	}
	container.innerHTML = config.monitors.map((m, i) => renderMonitorCard(m, i)).join("");
	bindCollapsibles(container);
	bindDynamicEvents(container);
}

function renderMonitorCard(m, idx) {
	const pulseProtocols = [
		"http",
		"ws",
		"tcp",
		"udp",
		"icmp",
		"smtp",
		"imap",
		"mysql",
		"mssql",
		"postgresql",
		"redis",
		"snmp",
		"dns",
		"minecraft-java",
		"minecraft-bedrock",
		"gamedig",
	];
	const currentProtocol = m.pulse ? Object.keys(m.pulse)[0] || "" : "";

	return `
<div class="config-card" data-monitor-idx="${idx}">
<div class="config-card-header">
<div class="config-card-title">
<h3>${m.name || "Unnamed Monitor"}</h3>
<span class="badge mono">${m.id || ""}</span>
</div>
<div class="config-card-actions">
<button class="btn-icon danger" data-action="remove-monitor" data-idx="${idx}" title="Delete">
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
</button>
</div>
</div>
<div class="form-grid">
<div class="form-group">
<label class="form-label">ID <span class="required">*</span></label>
<input class="form-input mono" type="text" value="${m.id || ""}" data-bind="monitors.${idx}.id" data-rerender="monitors" />
</div>
<div class="form-group">
<label class="form-label">Name <span class="required">*</span></label>
<input class="form-input" type="text" value="${esc(m.name || "")}" data-bind="monitors.${idx}.name" data-update-title />
</div>
<div class="form-group">
<label class="form-label">Token <span class="required">*</span></label>
<input class="form-input mono" type="text" value="${esc(m.token || "")}" data-bind="monitors.${idx}.token" />
</div>
<div class="form-group">
<label class="form-label">Interval (seconds) <span class="required">*</span></label>
<input class="form-input" type="number" value="${m.interval ?? ""}" min="1" data-bind="monitors.${idx}.interval" data-type="number" />
</div>
<div class="form-group">
<label class="form-label">Max Retries <span class="required">*</span></label>
<input class="form-input" type="number" value="${m.maxRetries ?? ""}" min="0" data-bind="monitors.${idx}.maxRetries" data-type="number" />
<span class="form-hint">0 = mark down immediately</span>
</div>
<div class="form-group">
<label class="form-label">Resend Notification <span class="required">*</span></label>
<input class="form-input" type="number" value="${m.resendNotification ?? ""}" min="0" data-bind="monitors.${idx}.resendNotification" data-type="number" />
<span class="form-hint">0 = never resend</span>
</div>
<div class="form-group">
<label class="form-label">Children</label>
${renderMultiSelect(`mon-ch-${idx}`, m.children || [], "monitors", idx, "children")}
<span class="form-hint">Child monitors/groups held by this monitor</span>
</div>
<div class="form-group">
<label class="form-label">Notification Channels</label>
${renderMultiSelect(`mon-nc-${idx}`, m.notificationChannels || [], "monitors", idx, "notificationChannels")}
</div>
<div class="form-group">
<label class="form-label">PulseMonitor Agents</label>
${renderMultiSelect(`mon-pm-${idx}`, m.pulseMonitors || [], "monitors", idx, "pulseMonitors")}
</div>
<div class="form-group">
<label class="form-label">Dependencies</label>
${renderMultiSelect(`mon-dep-${idx}`, m.dependencies || [], "monitors", idx, "dependencies")}
<span class="form-hint">Suppress notifications when dependencies are down</span>
</div>
</div>

<!-- Custom Metrics -->
<button class="collapsible-trigger" type="button">
<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
Custom Metrics
</button>
<div class="collapsible-content">
${[1, 2, 3]
	.map((n) => {
		const cm = m[`custom${n}`] || {};
		return `
<div class="form-section-title">Custom Metric ${n}</div>
<div class="form-grid">
	<div class="form-group">
		<label class="form-label">ID</label>
		<input class="form-input mono" type="text" value="${esc(cm.id || "")}" data-bind="monitors.${idx}.custom${n}.id" data-ensure="monitors.${idx}.custom${n}" />
	</div>
	<div class="form-group">
		<label class="form-label">Name</label>
		<input class="form-input" type="text" value="${esc(cm.name || "")}" data-bind="monitors.${idx}.custom${n}.name" data-ensure="monitors.${idx}.custom${n}" />
	</div>
	<div class="form-group">
		<label class="form-label">Unit</label>
		<input class="form-input" type="text" value="${esc(cm.unit || "")}" data-bind="monitors.${idx}.custom${n}.unit" data-ensure="monitors.${idx}.custom${n}" data-empty-undefined placeholder="e.g. MB, %, conn" />
	</div>
</div>`;
	})
	.join("")}
</div>

<!-- Pulse Configuration -->
<button class="collapsible-trigger" type="button">
<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
Pulse Configuration (PulseMonitor)
</button>
<div class="collapsible-content">
<div class="form-grid">
<div class="form-group">
	<label class="form-label">Protocol</label>
	<select class="form-select" data-action="set-pulse-protocol" data-idx="${idx}">
		<option value="">None</option>
		${pulseProtocols.map((p) => `<option value="${p}" ${currentProtocol === p ? "selected" : ""}>${p.toUpperCase()}</option>`).join("")}
	</select>
</div>
</div>
<div id="pulse-fields-${idx}">
${renderPulseFields(idx, m.pulse)}
</div>
</div>
</div>`;
}

function setPulseProtocol(idx, protocol) {
	if (!protocol) {
		delete config.monitors[idx].pulse;
	} else {
		config.monitors[idx].pulse = { [protocol]: {} };
	}
	const container = document.getElementById(`pulse-fields-${idx}`);
	container.innerHTML = renderPulseFields(idx, config.monitors[idx].pulse);
	bindDynamicEvents(container);
}

function renderPulseFields(idx, pulse) {
	if (!pulse) return "";
	const proto = Object.keys(pulse)[0];
	if (!proto) return "";
	const p = pulse[proto] || {};

	const fields = {
		http: [
			{ key: "method", label: "Method", type: "select", options: ["GET", "POST", "HEAD"], val: p.method },
			{ key: "url", label: "URL", type: "text", val: p.url },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 10 },
		],
		ws: [
			{ key: "url", label: "URL", type: "text", val: p.url },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 3 },
		],
		tcp: [
			{ key: "host", label: "Host", type: "text", val: p.host },
			{ key: "port", label: "Port", type: "number", val: p.port },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 5 },
		],
		udp: [
			{ key: "host", label: "Host", type: "text", val: p.host },
			{ key: "port", label: "Port", type: "number", val: p.port },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 3 },
			{ key: "payload", label: "Payload", type: "text", val: p.payload },
		],
		icmp: [
			{ key: "host", label: "Host", type: "text", val: p.host },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 3 },
		],
		smtp: [{ key: "url", label: "URL", type: "text", val: p.url, full: true }],
		imap: [
			{ key: "server", label: "Server", type: "text", val: p.server },
			{ key: "port", label: "Port", type: "number", val: p.port || 993 },
			{ key: "username", label: "Username", type: "text", val: p.username },
			{ key: "password", label: "Password", type: "text", val: p.password },
		],
		mysql: [
			{ key: "url", label: "URL", type: "text", val: p.url },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 3 },
		],
		mssql: [
			{ key: "url", label: "URL", type: "text", val: p.url },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 3 },
		],
		postgresql: [
			{ key: "url", label: "URL", type: "text", val: p.url },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 3 },
		],
		redis: [
			{ key: "url", label: "URL", type: "text", val: p.url },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 3 },
		],
		dns: [
			{ key: "host", label: "Host", type: "text", val: p.host },
			{ key: "port", label: "Port", type: "number", val: p.port || 53 },
			{ key: "query", label: "Query", type: "text", val: p.query, full: true },
			{
				key: "recordType",
				label: "Record Type",
				type: "select",
				options: ["A", "AAAA", "CAA", "CNAME", "MX", "NS", "PTR", "SOA", "SRV", "TXT", "ANY"],
				val: p.recordType || "A",
			},
			{ key: "protocol", label: "Protocol", type: "select", options: ["udp", "tcp"], val: p.protocol || "udp" },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 3 },
			{ key: "requireAnswer", label: "Require Answer", type: "check", val: p.requireAnswer !== false },
			{ key: "expectedValue", label: "Expected Value (substring)", type: "text", val: p.expectedValue, full: true },
		],
		"minecraft-java": [
			{ key: "host", label: "Host", type: "text", val: p.host },
			{ key: "port", label: "Port", type: "number", val: p.port || 25565 },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 3 },
		],
		"minecraft-bedrock": [
			{ key: "host", label: "Host", type: "text", val: p.host },
			{ key: "port", label: "Port", type: "number", val: p.port || 19132 },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 3 },
		],
		gamedig: [
			{
				key: "game",
				label: "Game ID",
				type: "text",
				val: p.game,
				emptyUndefined: true,
				placeholder: "e.g. valheim, rust, minecraftjava",
				hint: "GameDig game ID. Leave empty when using a generic protocol.",
			},
			{
				key: "protocol",
				label: "Generic Protocol",
				type: "select",
				options: ["", "valve", "gamespy1", "gamespy2", "gamespy3", "quake1", "quake2", "quake3", "unreal2"],
				val: p.protocol || "",
				emptyUndefined: true,
				hint: "For games without an ID. Requires a port.",
			},
			{ key: "host", label: "Host", type: "text", val: p.host },
			{ key: "port", label: "Query Port", type: "number", val: p.port, emptyUndefined: true, placeholder: "Game default" },
			{ key: "timeout", label: "Timeout (s)", type: "number", val: p.timeout || 5 },
		],
	};

	if (proto === "snmp") {
		return renderSnmpFields(idx, p);
	}

	const fieldSet = fields[proto] || [];
	return `<div class="form-grid" style="margin-top:12px">
${fieldSet
	.map((f) => {
		const fullClass = f.full ? " full-width" : "";
		const bindPath = `monitors.${idx}.pulse.${proto}.${f.key}`;
		const emptyAttr = f.emptyUndefined ? " data-empty-undefined" : "";
		const hint = f.hint ? `\n\t<span class="form-hint">${f.hint}</span>` : "";
		if (f.type === "select") {
			return `<div class="form-group${fullClass}">
	<label class="form-label">${f.label}</label>
	<select class="form-select" data-bind="${bindPath}"${emptyAttr}>
		${f.options.map((o) => `<option value="${o}" ${f.val === o ? "selected" : ""}>${o || "None"}</option>`).join("")}
	</select>${hint}
</div>`;
		}
		if (f.type === "check") {
			return `<div class="form-group${fullClass}">
    <label class="form-check">
        <input type="checkbox" ${f.val ? "checked" : ""} data-bind="${bindPath}" data-type="boolean" />
        <span class="form-check-label">${f.label}</span>
    </label>
</div>`;
		}
		return `<div class="form-group${fullClass}">
<label class="form-label">${f.label}</label>
<input class="form-input${f.type === "text" ? " mono" : ""}" type="${f.type}" value="${esc(f.val ?? "")}"
	data-bind="${bindPath}" ${f.type === "number" ? 'data-type="number"' : ""}${emptyAttr}${f.placeholder ? ` placeholder="${f.placeholder}"` : ""} />${hint}
</div>`;
	})
	.join("")}
</div>`;
}

function renderSnmpFields(idx, p) {
	const version = p.version || "3";
	const securityLevel = p.securityLevel || "authPriv";
	const oids = p.oids || {};
	const oidEntries = Object.entries(oids);

	const isV3 = version === "3";
	const showPriv = isV3 && securityLevel === "authPriv";
	const showAuth = isV3 && (securityLevel === "authNoPriv" || securityLevel === "authPriv");

	let html = `<div class="form-grid" style="margin-top:12px">
<div class="form-group">
	<label class="form-label">Host <span class="required">*</span></label>
	<input class="form-input mono" type="text" value="${esc(p.host || "")}"
		data-bind="monitors.${idx}.pulse.snmp.host" placeholder="192.168.1.1" />
</div>
<div class="form-group">
	<label class="form-label">Port</label>
	<input class="form-input" type="number" value="${p.port ?? 161}"
		data-bind="monitors.${idx}.pulse.snmp.port" data-type="number" />
</div>
<div class="form-group">
	<label class="form-label">Version</label>
	<select class="form-select" data-bind="monitors.${idx}.pulse.snmp.version" data-action="set-snmp-version" data-idx="${idx}">
		<option value="1" ${version === "1" ? "selected" : ""}>1</option>
		<option value="2c" ${version === "2c" ? "selected" : ""}>2c</option>
		<option value="3" ${version === "3" ? "selected" : ""}>3</option>
	</select>
</div>
<div class="form-group">
	<label class="form-label">Timeout (s)</label>
	<input class="form-input" type="number" value="${p.timeout ?? 3}"
		data-bind="monitors.${idx}.pulse.snmp.timeout" data-type="number" />
</div>
<div class="form-group full-width">
	<label class="form-label">Primary OID</label>
	<input class="form-input mono" type="text" value="${esc(p.oid || "1.3.6.1.2.1.1.3.0")}"
		data-bind="monitors.${idx}.pulse.snmp.oid" placeholder="1.3.6.1.2.1.1.3.0" />
	<span class="form-hint">OID for availability check (default: sysUpTime). Must be numeric dot-notation.</span>
</div>`;

	// v1/v2c: community string
	if (!isV3) {
		html += `
<div class="form-group full-width">
	<label class="form-label">Community</label>
	<input class="form-input mono" type="text" value="${esc(p.community || "public")}"
		data-bind="monitors.${idx}.pulse.snmp.community" placeholder="public" />
</div>`;
	}

	// v3: security settings
	if (isV3) {
		html += `
<div class="form-group">
	<label class="form-label">Security Level</label>
	<select class="form-select" data-bind="monitors.${idx}.pulse.snmp.securityLevel" data-action="set-snmp-version" data-idx="${idx}">
		<option value="noAuthNoPriv" ${securityLevel === "noAuthNoPriv" ? "selected" : ""}>noAuthNoPriv</option>
		<option value="authNoPriv" ${securityLevel === "authNoPriv" ? "selected" : ""}>authNoPriv</option>
		<option value="authPriv" ${securityLevel === "authPriv" ? "selected" : ""}>authPriv</option>
	</select>
</div>
<div class="form-group">
	<label class="form-label">Username <span class="required">*</span></label>
	<input class="form-input mono" type="text" value="${esc(p.username || "")}"
		data-bind="monitors.${idx}.pulse.snmp.username" />
</div>`;

		if (showAuth) {
			html += `
<div class="form-group">
	<label class="form-label">Auth Password <span class="required">*</span></label>
	<input class="form-input mono" type="text" value="${esc(p.authPassword || "")}"
		data-bind="monitors.${idx}.pulse.snmp.authPassword" />
</div>
<div class="form-group">
	<label class="form-label">Auth Protocol</label>
	<select class="form-select" data-bind="monitors.${idx}.pulse.snmp.authProtocol">
		${["md5", "sha1", "sha224", "sha256", "sha384", "sha512"].map((o) => `<option value="${o}" ${(p.authProtocol || "sha256") === o ? "selected" : ""}>${o}</option>`).join("")}
	</select>
</div>`;
		}

		if (showPriv) {
			html += `
<div class="form-group">
	<label class="form-label">Privacy Password <span class="required">*</span></label>
	<input class="form-input mono" type="text" value="${esc(p.privPassword || "")}"
		data-bind="monitors.${idx}.pulse.snmp.privPassword" />
</div>
<div class="form-group">
	<label class="form-label">Privacy Cipher</label>
	<select class="form-select" data-bind="monitors.${idx}.pulse.snmp.privCipher">
		${["des", "aes128", "aes192", "aes256"].map((o) => `<option value="${o}" ${(p.privCipher || "aes128") === o ? "selected" : ""}>${o}</option>`).join("")}
	</select>
</div>`;
		}
	}

	html += `</div>`;

	// OIDs editor
	html += `
<div class="form-section-title" style="margin-top:16px">Custom OIDs</div>
<span class="form-hint" style="display:block;margin-bottom:8px">Map placeholder names to OIDs. Entries named custom1, custom2, custom3 populate custom metric fields.</span>
<div id="snmp-oids-${idx}">
${renderSnmpOidRows(idx, oidEntries)}
</div>
<button class="btn btn-secondary btn-sm" data-action="add-snmp-oid" data-idx="${idx}" style="margin-top:8px">
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
		<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
	</svg>
	Add OID
</button>`;

	return html;
}

function renderSnmpOidRows(idx, entries) {
	if (entries.length === 0) return "";
	return entries
		.map(
			([key, val], i) => `
<div style="display:flex;gap:8px;align-items:center;margin-bottom:6px">
	<input class="form-input mono" type="text" value="${esc(key)}" placeholder="custom1"
		data-action="snmp-oid-key" data-idx="${idx}" data-oid-idx="${i}" style="flex:1" />
	<input class="form-input mono" type="text" value="${esc(val)}" placeholder="1.3.6.1.4.1.2021.11.11.0"
		data-action="snmp-oid-val" data-idx="${idx}" data-oid-idx="${i}" style="flex:2" />
	<button class="btn-icon danger" data-action="remove-snmp-oid" data-idx="${idx}" data-oid-idx="${i}" title="Remove OID">
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
	</button>
</div>`,
		)
		.join("");
}

function flushSnmpOids(idx) {
	const container = document.getElementById(`snmp-oids-${idx}`);
	if (!container) return;
	const keys = container.querySelectorAll('[data-action="snmp-oid-key"]');
	const vals = container.querySelectorAll('[data-action="snmp-oid-val"]');
	const oids = {};
	keys.forEach((keyEl, i) => {
		const k = keyEl.value.trim();
		const v = vals[i]?.value.trim() || "";
		if (k) oids[k] = v;
	});
	if (!config.monitors[idx].pulse) return;
	if (!config.monitors[idx].pulse.snmp) return;
	if (Object.keys(oids).length > 0) {
		config.monitors[idx].pulse.snmp.oids = oids;
	} else {
		delete config.monitors[idx].pulse.snmp.oids;
	}
}

function addSnmpOid(idx) {
	flushSnmpOids(idx);
	const snmp = config.monitors[idx]?.pulse?.snmp;
	if (!snmp) return;
	if (!snmp.oids) snmp.oids = {};
	// Find next available custom key
	let nextKey = "";
	for (let n = 1; n <= 3; n++) {
		if (!snmp.oids[`custom${n}`]) {
			nextKey = `custom${n}`;
			break;
		}
	}
	snmp.oids[nextKey] = "";
	const container = document.getElementById(`snmp-oids-${idx}`);
	container.innerHTML = renderSnmpOidRows(idx, Object.entries(snmp.oids));
	bindSnmpOidEvents(container);
}

function removeSnmpOid(idx, oidIdx) {
	flushSnmpOids(idx);
	const snmp = config.monitors[idx]?.pulse?.snmp;
	if (!snmp?.oids) return;
	const entries = Object.entries(snmp.oids);
	entries.splice(oidIdx, 1);
	snmp.oids = Object.fromEntries(entries);
	if (Object.keys(snmp.oids).length === 0) delete snmp.oids;
	const container = document.getElementById(`snmp-oids-${idx}`);
	container.innerHTML = renderSnmpOidRows(idx, Object.entries(snmp.oids || {}));
	bindSnmpOidEvents(container);
}

function reRenderSnmpFields(idx) {
	flushBindings(document.getElementById(`pulse-fields-${idx}`));
	flushSnmpOids(idx);
	const container = document.getElementById(`pulse-fields-${idx}`);
	container.innerHTML = renderPulseFields(idx, config.monitors[idx].pulse);
	bindDynamicEvents(container);
	bindSnmpOidEvents(container);
}

function bindSnmpOidEvents(container) {
	container.querySelectorAll('[data-action="snmp-oid-key"], [data-action="snmp-oid-val"]').forEach((el) => {
		el.addEventListener("change", () => {
			flushSnmpOids(Number(el.dataset.idx));
		});
	});
}

function addGroup() {
	flushBindings(document.getElementById("groups-list"));
	config.groups.push({
		id: uid(),
		name: "",
		strategy: "percentage",
		degradedThreshold: 50,
		interval: 60,
		resendNotification: 0,
	});
	renderGroups();
	renderMonitors();
	updateBadges();
}

function removeGroup(idx) {
	flushBindings(document.getElementById("groups-list"));
	const removed = config.groups[idx];
	const removedId = removed?.id;
	config.groups.splice(idx, 1);
	pruneDependencyRefs(removedId);
	pruneChildrenRefs(removedId);
	pruneStatusPageItemRefs(removedId);
	renderGroups();
	renderMonitors();
	renderStatusPages();
	updateBadges();
}

function renderGroups() {
	const container = document.getElementById("groups-list");

	if (config.groups.length === 0) {
		container.innerHTML = `
<div class="empty-state">
<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
<h3>No Groups</h3>
<p>Groups let you organize monitors hierarchically.</p>
</div>`;
		return;
	}
	container.innerHTML = config.groups
		.map((g, i) => {
			return `
<div class="config-card">
<div class="config-card-header">
<div class="config-card-title">
	<h3>${g.name || "Unnamed Group"}</h3>
	<span class="badge mono">${g.id || ""}</span>
</div>
<div class="config-card-actions">
	<button class="btn-icon danger" data-action="remove-group" data-idx="${i}" title="Delete">
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
	</button>
</div>
</div>
<div class="form-grid">
<div class="form-group">
	<label class="form-label">ID <span class="required">*</span></label>
	<input class="form-input mono" type="text" value="${g.id || ""}" data-bind="groups.${i}.id" data-rerender="groups" />
</div>
<div class="form-group">
	<label class="form-label">Name <span class="required">*</span></label>
	<input class="form-input" type="text" value="${esc(g.name || "")}" data-bind="groups.${i}.name" data-update-title data-rerender="groups" />
</div>
<div class="form-group">
	<label class="form-label">Strategy <span class="required">*</span></label>
	<select class="form-select" data-bind="groups.${i}.strategy">
		<option value="any-up" ${g.strategy === "any-up" ? "selected" : ""}>any-up</option>
		<option value="all-up" ${g.strategy === "all-up" ? "selected" : ""}>all-up</option>
		<option value="percentage" ${g.strategy === "percentage" ? "selected" : ""}>percentage</option>
	</select>
</div>
<div class="form-group">
	<label class="form-label">Degraded Threshold (%) <span class="required">*</span></label>
	<input class="form-input" type="number" value="${g.degradedThreshold ?? ""}" min="0" max="100" data-bind="groups.${i}.degradedThreshold" data-type="number" />
</div>
<div class="form-group">
	<label class="form-label">Interval (seconds) <span class="required">*</span></label>
	<input class="form-input" type="number" value="${g.interval ?? ""}" min="1" data-bind="groups.${i}.interval" data-type="number" />
</div>
<div class="form-group">
	<label class="form-label">Resend Notification</label>
	<input class="form-input" type="number" value="${g.resendNotification ?? ""}" min="0" data-bind="groups.${i}.resendNotification" data-type="number" />
</div>
<div class="form-group">
	<label class="form-label">Children</label>
	${renderMultiSelect(`grp-ch-${i}`, g.children || [], "groups", i, "children")}
	<span class="form-hint">Child monitors/groups held by this group</span>
</div>
<div class="form-group">
	<label class="form-label">Notification Channels</label>
	${renderMultiSelect(`grp-nc-${i}`, g.notificationChannels || [], "groups", i, "notificationChannels")}
</div>
<div class="form-group">
	<label class="form-label">Dependencies</label>
	${renderMultiSelect(`grp-dep-${i}`, g.dependencies || [], "groups", i, "dependencies")}
	<span class="form-hint">Suppress notifications when dependencies are down</span>
</div>
</div>
</div>`;
		})
		.join("");
	bindDynamicEvents(container);
}

function addStatusPage() {
	flushBindings(document.getElementById("statuspages-list"));
	config.status_pages.push({
		id: uid(),
		name: "",
		slug: "",
		items: [],
	});
	renderStatusPages();
	updateBadges();
}

function removeStatusPage(idx) {
	flushBindings(document.getElementById("statuspages-list"));
	config.status_pages.splice(idx, 1);
	renderStatusPages();
	updateBadges();
}

function renderStatusPages() {
	const container = document.getElementById("statuspages-list");

	if (config.status_pages.length === 0) {
		container.innerHTML = `
<div class="empty-state">
<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
<h3>No Status Pages</h3>
<p>Status pages display the health of your monitors and groups.</p>
</div>`;
		return;
	}
	container.innerHTML = config.status_pages
		.map(
			(sp, i) => `
<div class="config-card">
<div class="config-card-header">
<div class="config-card-title">
	<h3>${sp.name || "Unnamed Status Page"}</h3>
	<span class="badge mono">/${sp.slug || "..."}</span>
</div>
<div class="config-card-actions">
	<button class="btn-icon danger" data-action="remove-statuspage" data-idx="${i}" title="Delete">
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
	</button>
</div>
</div>
<div class="form-grid">
<div class="form-group">
	<label class="form-label">ID <span class="required">*</span></label>
	<input class="form-input mono" type="text" value="${sp.id || ""}" data-bind="status_pages.${i}.id" />
</div>
<div class="form-group">
	<label class="form-label">Name <span class="required">*</span></label>
	<input class="form-input" type="text" value="${esc(sp.name || "")}" data-bind="status_pages.${i}.name" data-update-title />
</div>
<div class="form-group">
	<label class="form-label">Slug <span class="required">*</span></label>
	<input class="form-input mono" type="text" value="${esc(sp.slug || "")}" data-bind="status_pages.${i}.slug" placeholder="lowercase-with-hyphens" />
	<span class="form-hint">URL path: /v1/status/{slug}</span>
</div>
<div class="form-group">
	<label class="form-label">Password</label>
	<input class="form-input" type="text" value="${esc(sp.password || "")}" data-bind="status_pages.${i}.password" data-empty-undefined placeholder="Optional (min 8 chars)" />
</div>
<div class="form-group">
	<label class="form-check">
		<input type="checkbox" ${sp.reports ? "checked" : ""} data-bind="status_pages.${i}.reports" data-type="boolean" />
		<span class="form-check-label">Enable Reports</span>
	</label>
	<span class="form-hint">Allow exporting monitor and group history as CSV</span>
</div>
<div class="form-group full-width">
	<label class="form-label">Items (Monitor/Group IDs) <span class="required">*</span></label>
	${renderMultiSelect(`sp-items-${i}`, sp.items || [], "status_pages", i, "items")}
</div>
<div class="form-group full-width">
	<label class="form-label">Leaf Items (Monitor/Group IDs)</label>
	${renderMultiSelect(`sp-leafItems-${i}`, sp.leafItems || [], "status_pages", i, "leafItems")}
	<span class="form-hint">Children of these items won't be expanded on the status page</span>
</div>
</div>
</div>
`,
		)
		.join("");
	bindDynamicEvents(container);
	updateGraphFilterOptions();
}

function addNotificationChannel() {
	flushBindings(document.getElementById("notifications-list"));
	const id = uid();
	if (!config.notifications) config.notifications = { channels: {} };
	if (!config.notifications.channels) config.notifications.channels = {};
	config.notifications.channels[id] = {
		id: id,
		name: "",
		enabled: true,
	};
	renderNotifications();
	updateBadges();
}

function removeNotificationChannel(key) {
	flushBindings(document.getElementById("notifications-list"));
	const removed = config.notifications?.channels?.[key];
	const removedId = removed?.id || key;

	delete config.notifications.channels[key];

	pruneNotificationChannelRefs(removedId, key);

	const ml = document.getElementById("monitors-list");
	const gl = document.getElementById("groups-list");
	if (ml) flushBindings(ml);
	if (gl) flushBindings(gl);

	renderNotifications();
	renderGroups();
	renderMonitors();
	updateBadges();
}

function renderNotifications() {
	const container = document.getElementById("notifications-list");

	const channels = config.notifications?.channels || {};
	const keys = Object.keys(channels);

	if (keys.length === 0) {
		container.innerHTML = `
<div class="empty-state">
<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
<h3>No Notification Channels</h3>
<p>Configure Discord, Email, Ntfy, Telegram or Webhook notifications.</p>
</div>`;
		return;
	}

	container.innerHTML = keys
		.map((key) => {
			const ch = channels[key];
			return `
<div class="config-card">
<div class="config-card-header">
<div class="config-card-title">
	<h3>${ch.name || "Unnamed Channel"}</h3>
	<span class="badge mono">${ch.id || key}</span>
</div>
<div class="config-card-actions">
	<button class="btn-icon danger" data-action="remove-notification" data-key="${key}" title="Delete">
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
	</button>
</div>
</div>
<div class="form-grid">
<div class="form-group">
	<label class="form-label">ID <span class="required">*</span></label>
	<input class="form-input mono" type="text" value="${esc(ch.id || key)}" data-action="update-channel-id" data-key="${key}" />
</div>
<div class="form-group">
	<label class="form-label">Name <span class="required">*</span></label>
	<input class="form-input" type="text" value="${esc(ch.name || "")}" data-bind="notifications.channels.${key}.name" data-update-title />
</div>
<div class="form-group">
	<label class="form-label">Description</label>
	<input class="form-input" type="text" value="${esc(ch.description || "")}" data-bind="notifications.channels.${key}.description" data-empty-undefined />
</div>
<div class="form-group">
	<label class="form-check">
		<input type="checkbox" ${ch.enabled ? "checked" : ""} data-bind="notifications.channels.${key}.enabled" data-type="boolean" />
		<span class="form-check-label">Enabled</span>
	</label>
</div>
</div>

<!-- Discord -->
<hr class="form-divider"/>
<div class="form-section-title">Discord</div>
<div class="form-grid">
<div class="form-group">
	<label class="form-check">
		<input type="checkbox" ${ch.discord?.enabled ? "checked" : ""} data-bind="notifications.channels.${key}.discord.enabled" data-type="boolean" data-ensure="notifications.channels.${key}.discord" />
		<span class="form-check-label">Enable Discord</span>
	</label>
</div>
<div class="form-group full-width">
	<label class="form-label">Webhook URL</label>
	<input class="form-input mono" type="text" value="${esc(ch.discord?.webhookUrl || "")}" placeholder="https://discord.com/api/webhooks/..."
		data-bind="notifications.channels.${key}.discord.webhookUrl" data-ensure="notifications.channels.${key}.discord" />
</div>
<div class="form-group">
	<label class="form-label">Username</label>
	<input class="form-input" type="text" value="${esc(ch.discord?.username || "")}" placeholder="Uptime Bot"
		data-bind="notifications.channels.${key}.discord.username" data-ensure="notifications.channels.${key}.discord" data-empty-undefined />
</div>
<div class="form-group">
	<label class="form-label">Avatar URL</label>
	<input class="form-input mono" type="text" value="${esc(ch.discord?.avatarUrl || "")}"
		data-bind="notifications.channels.${key}.discord.avatarUrl" data-ensure="notifications.channels.${key}.discord" data-empty-undefined />
</div>
</div>

<!-- Email -->
<hr class="form-divider"/>
<div class="form-section-title">Email (SMTP)</div>
<div class="form-grid">
<div class="form-group">
	<label class="form-check">
		<input type="checkbox" ${ch.email?.enabled ? "checked" : ""} data-bind="notifications.channels.${key}.email.enabled" data-type="boolean" data-ensure="notifications.channels.${key}.email" />
		<span class="form-check-label">Enable Email</span>
	</label>
</div>
<div class="form-group">
	<label class="form-label">From</label>
	<input class="form-input" type="text" value="${esc(ch.email?.from || "")}" placeholder='"Uptime Monitor" <alerts@example.com>'
		data-bind="notifications.channels.${key}.email.from" data-ensure="notifications.channels.${key}.email" />
</div>
<div class="form-group">
	<label class="form-label">To (comma-separated)</label>
	<input class="form-input" type="text" value="${(ch.email?.to || []).join(", ")}"
		data-bind="notifications.channels.${key}.email.to" data-type="csv" data-ensure="notifications.channels.${key}.email" />
</div>
<div class="form-group">
	<label class="form-label">SMTP Host</label>
	<input class="form-input mono" type="text" value="${esc(ch.email?.smtp?.host || "")}"
		data-bind="notifications.channels.${key}.email.smtp.host" data-ensure="notifications.channels.${key}.email.smtp" />
</div>
<div class="form-group">
	<label class="form-label">SMTP Port</label>
	<input class="form-input" type="number" value="${ch.email?.smtp?.port || ""}"
		data-bind="notifications.channels.${key}.email.smtp.port" data-type="number" data-ensure="notifications.channels.${key}.email.smtp" />
</div>
<div class="form-group">
	<label class="form-check">
		<input type="checkbox" ${ch.email?.smtp?.secure ? "checked" : ""} data-bind="notifications.channels.${key}.email.smtp.secure" data-type="boolean" data-ensure="notifications.channels.${key}.email.smtp" />
		<span class="form-check-label">Use TLS (secure)</span>
	</label>
</div>
<div class="form-group">
	<label class="form-label">SMTP User</label>
	<input class="form-input" type="text" value="${esc(ch.email?.smtp?.user || "")}"
		data-bind="notifications.channels.${key}.email.smtp.user" data-ensure="notifications.channels.${key}.email.smtp" />
</div>
<div class="form-group">
	<label class="form-label">SMTP Password</label>
	<input class="form-input" type="password" value="${esc(ch.email?.smtp?.pass || "")}"
		data-bind="notifications.channels.${key}.email.smtp.pass" data-ensure="notifications.channels.${key}.email.smtp" />
</div>
</div>

<!-- Ntfy -->
<hr class="form-divider"/>
<div class="form-section-title">Ntfy</div>
<div class="form-grid">
<div class="form-group">
	<label class="form-check">
		<input type="checkbox" ${ch.ntfy?.enabled ? "checked" : ""} data-bind="notifications.channels.${key}.ntfy.enabled" data-type="boolean" data-ensure="notifications.channels.${key}.ntfy" />
		<span class="form-check-label">Enable Ntfy</span>
	</label>
</div>
<div class="form-group">
	<label class="form-label">Server</label>
	<input class="form-input mono" type="text" value="${esc(ch.ntfy?.server || "")}" placeholder="https://ntfy.sh"
		data-bind="notifications.channels.${key}.ntfy.server" data-ensure="notifications.channels.${key}.ntfy" />
</div>
<div class="form-group">
	<label class="form-label">Topic</label>
	<input class="form-input mono" type="text" value="${esc(ch.ntfy?.topic || "")}"
		data-bind="notifications.channels.${key}.ntfy.topic" data-ensure="notifications.channels.${key}.ntfy" />
</div>
<div class="form-group">
	<label class="form-label">Token</label>
	<input class="form-input mono" type="text" value="${esc(ch.ntfy?.token || "")}" placeholder="Optional"
		data-bind="notifications.channels.${key}.ntfy.token" data-ensure="notifications.channels.${key}.ntfy" data-empty-undefined />
</div>
</div>

<!-- Telegram -->
<hr class="form-divider"/>
<div class="form-section-title">Telegram</div>
<div class="form-grid">
<div class="form-group">
	<label class="form-check">
		<input type="checkbox" ${ch.telegram?.enabled ? "checked" : ""} data-bind="notifications.channels.${key}.telegram.enabled" data-type="boolean" data-ensure="notifications.channels.${key}.telegram" />
		<span class="form-check-label">Enable Telegram</span>
	</label>
</div>
<div class="form-group">
	<label class="form-label">Bot Token</label>
	<input class="form-input mono" type="text" value="${esc(ch.telegram?.botToken || "")}" placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
		data-bind="notifications.channels.${key}.telegram.botToken" data-ensure="notifications.channels.${key}.telegram" />
	<span class="form-hint">Get from @BotFather on Telegram</span>
</div>
<div class="form-group">
	<label class="form-label">Chat ID</label>
	<input class="form-input mono" type="text" value="${esc(ch.telegram?.chatId || "")}" placeholder="-1001234567890"
		data-bind="notifications.channels.${key}.telegram.chatId" data-ensure="notifications.channels.${key}.telegram" />
	<span class="form-hint">User, group or channel ID</span>
</div>
<div class="form-group">
	<label class="form-label">Topic ID</label>
	<input class="form-input" type="number" value="${ch.telegram?.topicId ?? ""}"
		data-bind="notifications.channels.${key}.telegram.topicId" data-type="number" data-ensure="notifications.channels.${key}.telegram" data-empty-undefined />
	<span class="form-hint">Optional - for forum groups with topics</span>
</div>
<div class="form-group">
	<label class="form-check">
		<input type="checkbox" ${ch.telegram?.disableNotification ? "checked" : ""} data-bind="notifications.channels.${key}.telegram.disableNotification" data-type="boolean" data-ensure="notifications.channels.${key}.telegram" data-empty-undefined />
		<span class="form-check-label">Send silently (no notification sound)</span>
	</label>
</div>
</div>

<!-- Webhook -->
<hr class="form-divider"/>
<div class="form-section-title">Webhook</div>
<div class="form-grid">
<div class="form-group">
	<label class="form-check">
		<input type="checkbox" ${ch.webhook?.enabled ? "checked" : ""} data-bind="notifications.channels.${key}.webhook.enabled" data-type="boolean" data-ensure="notifications.channels.${key}.webhook" />
		<span class="form-check-label">Enable Webhook</span>
	</label>
</div>
<div class="form-group full-width">
	<label class="form-label">URL</label>
	<input class="form-input mono" type="text" value="${esc(ch.webhook?.url || "")}" placeholder="https://example.com/webhook"
		data-bind="notifications.channels.${key}.webhook.url" data-ensure="notifications.channels.${key}.webhook" />
	<span class="form-hint">Notifications are sent as JSON POST requests</span>
</div>
<div class="form-group full-width">
	<label class="form-label">Custom Headers (one per line, format: Header: Value)</label>
	<textarea class="form-input mono" rows="3" placeholder="Authorization: Bearer your-token&#10;X-Api-Key: your-key"
		data-action="webhook-headers" data-key="${key}">${
			ch.webhook?.headers
				? Object.entries(ch.webhook.headers)
						.map(([k, v]) => k + ": " + v)
						.join("\n")
				: ""
		}</textarea>
	<span class="form-hint">Optional - use for authentication</span>
</div>
</div>
</div>`;
		})
		.join("");
	bindDynamicEvents(container);
}

function addPulseMonitor() {
	flushBindings(document.getElementById("pulsemonitors-list"));
	config.PulseMonitors.push({
		id: uid(),
		name: "",
		token: token(),
	});
	renderPulseMonitors();
	updateBadges();
}

function removePulseMonitor(idx) {
	flushBindings(document.getElementById("pulsemonitors-list"));

	const ml = document.getElementById("monitors-list");
	const gl = document.getElementById("groups-list");
	if (ml) flushBindings(ml);
	if (gl) flushBindings(gl);

	const removed = config.PulseMonitors?.[idx];
	const removedId = removed?.id;

	config.PulseMonitors.splice(idx, 1);

	prunePulseMonitorRefs(removedId);

	renderPulseMonitors();
	renderMonitors();
	renderGroups();
	updateBadges();
}

function renderPulseMonitors() {
	const container = document.getElementById("pulsemonitors-list");

	if (config.PulseMonitors.length === 0) {
		container.innerHTML = `
<div class="empty-state">
<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
<h3>No PulseMonitors</h3>
<p>PulseMonitor agents perform distributed health checks from multiple regions.</p>
</div>`;
		return;
	}
	container.innerHTML = config.PulseMonitors.map(
		(pm, i) => `
<div class="config-card">
<div class="config-card-header">
<div class="config-card-title">
	<h3>${pm.name || "Unnamed PulseMonitor"}</h3>
	<span class="badge mono">${pm.id || ""}</span>
</div>
<div class="config-card-actions">
	<button class="btn-icon danger" data-action="remove-pulsemonitor" data-idx="${i}" title="Delete">
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
	</button>
</div>
</div>
<div class="form-grid">
<div class="form-group">
	<label class="form-label">ID <span class="required">*</span></label>
	<input class="form-input mono" type="text" value="${esc(pm.id || "")}" data-bind="PulseMonitors.${i}.id" />
</div>
<div class="form-group">
	<label class="form-label">Name <span class="required">*</span></label>
	<input class="form-input" type="text" value="${esc(pm.name || "")}" data-bind="PulseMonitors.${i}.name" data-update-title />
</div>
<div class="form-group">
	<label class="form-label">Token <span class="required">*</span></label>
	<input class="form-input mono" type="text" value="${esc(pm.token || "")}" data-bind="PulseMonitors.${i}.token" />
</div>
</div>
</div>
`,
	).join("");
	bindDynamicEvents(container);
}

function getAvailableOptions(optionType) {
	switch (optionType) {
		case "notificationChannels": {
			const channels = config.notifications?.channels || {};
			return Object.keys(channels).map((key) => {
				const ch = channels[key];
				return { value: ch.id || key, label: ch.name ? `${ch.name} (${ch.id || key})` : ch.id || key };
			});
		}
		case "pulseMonitors": {
			return config.PulseMonitors.filter((pm) => pm.id).map((pm) => ({
				value: pm.id,
				label: pm.name ? `${pm.name} (${pm.id})` : pm.id,
			}));
		}
		case "children":
		case "dependencies": {
			const depMonOpts = config.monitors
				.filter((m) => m.id)
				.map((m) => ({
					value: m.id,
					label: m.name ? `${m.name} (${m.id})` : m.id,
				}));

			const depGrpOpts = config.groups
				.filter((g) => g.id)
				.map((g) => ({
					value: g.id,
					label: g.name ? `${g.name} (${g.id})` : g.id,
				}));

			return [...depGrpOpts, ...depMonOpts];
		}
		case "leafItems":
		case "items": {
			const monitorOpts = config.monitors
				.filter((m) => m.id)
				.map((m) => ({
					value: m.id,
					label: m.name ? `${m.name} (${m.id})` : m.id,
				}));

			const groupOpts = config.groups
				.filter((g) => g.id)
				.map((g) => ({
					value: g.id,
					label: g.name ? `${g.name} (${g.id})` : g.id,
				}));

			return [...groupOpts, ...monitorOpts];
		}
		default:
			return [];
	}
}

function renderMultiSelect(id, selectedValues, section, idx, prop) {
	let options = getAvailableOptions(prop);
	const selected = selectedValues || [];

	// Prevent self-references: exclude the current entity's own ID
	if (prop === "dependencies" || prop === "children") {
		const item = config[section]?.[idx];
		if (item?.id) {
			options = options.filter((o) => o.value !== item.id);
		}
	}

	const selectedTags = selected
		.map((v) => {
			const opt = options.find((o) => o.value === v);
			const displayLabel = opt ? opt.label : v;
			return `<span class="tag">${esc(displayLabel)}<button class="tag-remove" data-action="remove-tag" data-section="${section}" data-idx="${idx}" data-prop="${prop}" data-value="${esc(v)}">&times;</button></span>`;
		})
		.join("");

	const availableOptions = options.filter((o) => !selected.includes(o.value));
	const hasOptions = availableOptions.length > 0;
	const placeholderText =
		!hasOptions && options.length === 0
			? `No ${prop === "notificationChannels" ? "notification channels" : prop === "pulseMonitors" ? "PulseMonitor agents" : prop === "dependencies" || prop === "children" ? "monitors/groups" : "monitors/groups"} defined`
			: !hasOptions
				? "All options selected"
				: "Select to add...";

	const optionsHtml = availableOptions.map((o) => `<option value="${esc(o.value)}">${esc(o.label)}</option>`).join("");

	return `<div class="multi-select-container">
<div class="multi-select-tags">${selectedTags}</div>
<select class="form-select multi-select-dropdown" data-action="add-from-select" data-section="${section}" data-idx="${idx}" data-prop="${prop}" id="${id}" ${!hasOptions ? "disabled" : ""}>
	<option value="">${placeholderText}</option>
	${optionsHtml}
</select>
</div>`;
}

function addTag(section, idx, prop, value) {
	const item = config[section][idx];
	if (!item[prop]) item[prop] = [];
	if (!item[prop].includes(value)) item[prop].push(value);
	reRenderSection(section);
}

function removeTag(section, idx, prop, value) {
	const item = config[section][idx];
	if (item[prop]) item[prop] = item[prop].filter((v) => v !== value);
	// Removing from status page items should also remove from leafItems
	if (section === "status_pages" && prop === "items" && Array.isArray(item.leafItems)) {
		item.leafItems = item.leafItems.filter((v) => v !== value);
		if (item.leafItems.length === 0) delete item.leafItems;
	}
	reRenderSection(section);
}

function reRenderSection(section) {
	if (section === "monitors") renderMonitors();
	else if (section === "groups") {
		// Keep monitor edits and refresh group dropdown options
		const ml = document.getElementById("monitors-list");
		if (ml) flushBindings(ml);
		renderGroups();
		renderMonitors();
	} else if (section === "status_pages") renderStatusPages();
	updateBadges();
}

function pruneEmptyCustomMetrics(cfg) {
	for (const m of cfg.monitors || []) {
		for (let n = 1; n <= 3; n++) {
			const key = `custom${n}`;
			const cm = m[key];

			if (!cm) continue;

			if (!cm.id || String(cm.id).trim() === "") {
				delete m[key];
				continue;
			}

			if (cm.unit !== undefined && String(cm.unit).trim() === "") {
				delete cm.unit;
			}
		}
	}
}

function pruneDisabledProviders(cfg) {
	const providers = ["discord", "email", "ntfy", "telegram", "webhook"];
	const channels = cfg.notifications?.channels || {};
	for (const ch of Object.values(channels)) {
		for (const provider of providers) {
			if (ch[provider] && !ch[provider].enabled) {
				delete ch[provider];
			}
		}
	}
}

function exportConfig() {
	flushActiveTabEdits();

	const toExport = structuredClone(config);

	pruneEmptyCustomMetrics(toExport);
	pruneDisabledProviders(toExport);

	const toml = TOML.stringify(toExport);

	const blob = new Blob([toml], { type: "text/plain" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = "config.toml";
	a.click();
	URL.revokeObjectURL(url);
	showToast("Configuration exported as config.toml");
}

function resetConfig() {
	if (!confirm("Reset all configuration? This cannot be undone.")) return;
	config = {
		database: {},
		clickhouse: { url: "" },
		server: {},
		adminAPI: {},
		logger: {},
		missingPulseDetector: {},
		selfMonitoring: {},
		monitors: [],
		groups: [],
		status_pages: [],
		notifications: { channels: {} },
		PulseMonitors: [],
	};
	loadConfigToUI();
	showToast("Configuration reset");
}

function openServerSyncModal() {
	const saved = loadSyncCredentials();
	if (saved) {
		document.getElementById("sync-server-url").value = saved.url || "";
		document.getElementById("sync-api-token").value = saved.token || "";
		document.getElementById("sync-remember").checked = true;
	}
	document.getElementById("serverSyncModal").classList.add("open");
}

function closeServerSyncModal() {
	document.getElementById("serverSyncModal").classList.remove("open");
}

function toggleTokenVisibility() {
	const input = document.getElementById("sync-api-token");
	input.type = input.type === "password" ? "text" : "password";
}

function getSyncCredentials() {
	const url = document.getElementById("sync-server-url").value.trim().replace(/\/+$/, "");
	const token = document.getElementById("sync-api-token").value.trim();

	if (!url || !token) {
		showToast("Please enter both Server URL and Admin API Token", true);
		return null;
	}

	try {
		new URL(url);
	} catch {
		showToast("Invalid server URL", true);
		return null;
	}

	if (document.getElementById("sync-remember").checked) {
		saveSyncCredentials(url, token);
	} else {
		localStorage.removeItem("uptimemonitor-sync-credentials");
	}

	return { url, token };
}

function saveSyncCredentials(url, token) {
	try {
		localStorage.setItem("uptimemonitor-sync-credentials", JSON.stringify({ url, token }));
	} catch {}
}

function loadSyncCredentials() {
	try {
		const raw = localStorage.getItem("uptimemonitor-sync-credentials");
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

function setSyncLoading(btnId, loading) {
	const btn = document.getElementById(btnId);
	if (loading) {
		btn.classList.add("loading");
	} else {
		btn.classList.remove("loading");
	}
}

async function pullFromServer() {
	const creds = getSyncCredentials();
	if (!creds) {
		closeServerSyncModal();
		return;
	}

	setSyncLoading("btn-pull", true);

	try {
		const res = await fetch(`${creds.url}/v1/admin/config?format=toml`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${creds.token}`,
			},
		});

		if (!res.ok) {
			const errBody = await res.text();
			let detail;
			try {
				detail = JSON.parse(errBody)?.error || errBody;
			} catch {
				detail = errBody;
			}
			throw new Error(`Server responded ${res.status}: ${detail}`);
		}

		const toml = await res.text();
		parseTOML(toml);
		closeServerSyncModal();
		showToast("Configuration pulled from server!");
	} catch (err) {
		closeServerSyncModal();
		showToast("Pull failed: " + err.message, true);
	} finally {
		setSyncLoading("btn-pull", false);
	}
}

async function pushToServer() {
	const creds = getSyncCredentials();
	if (!creds) {
		closeServerSyncModal();
		return;
	}

	if (!confirm("This will overwrite the server configuration and trigger a reload. Continue?")) return;

	flushActiveTabEdits();

	const toExport = structuredClone(config);
	pruneEmptyCustomMetrics(toExport);
	pruneDisabledProviders(toExport);

	const toml = TOML.stringify(toExport);

	setSyncLoading("btn-push", true);

	try {
		const res = await fetch(`${creds.url}/v1/admin/config?format=toml`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${creds.token}`,
				"Content-Type": "application/toml; charset=utf-8",
			},
			body: toml,
		});

		if (!res.ok) {
			const errBody = await res.json().catch(() => ({}));
			const detail = errBody.details || errBody.error || `Status ${res.status}`;
			throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
		}

		closeServerSyncModal();
		showToast("Configuration pushed to server!");
	} catch (err) {
		closeServerSyncModal();
		showToast("Push failed: " + err.message, true);
	} finally {
		setSyncLoading("btn-push", false);
	}
}

// Close modal on overlay click
document.getElementById("serverSyncModal").addEventListener("click", (e) => {
	if (e.target === e.currentTarget) closeServerSyncModal();
});

// Close modal on Escape
document.addEventListener("keydown", (e) => {
	if (e.key === "Escape" && document.getElementById("serverSyncModal").classList.contains("open")) {
		closeServerSyncModal();
	}
});

function loadExample() {
	const example = `# Uptime Monitor - Example Configuration

[database]
url = "sqlite://./databases/uptime_monitor.db"

[clickhouse]
url = "http://uptime_user:uptime_password@clickhouse:8123/uptime_monitor"

[server]
port = 3000
proxy = "direct"

[adminAPI]
enabled = false
token = "admin_secret_token_example"

[logger]
level = 4

[missingPulseDetector]
interval = 5

[selfMonitoring]
enabled = true
id = "self-monitor"
interval = 3
backfillOnRecovery = true
latencyStrategy = "last-known"

# PulseMonitor Agents
[[PulseMonitors]]
id = "US-WEST-1"
name = "US West (Oregon)"
token = "tk_pulse_us_west_secret"

[[PulseMonitors]]
id = "EU-CENTRAL-1"
name = "EU Central (Frankfurt)"
token = "tk_pulse_eu_central_secret"

# Monitors
[[monitors]]
id = "api-prod"
name = "Production API"
token = "tk_prod_api_abc123"
interval = 30
maxRetries = 2
resendNotification = 12
notificationChannels = ["critical"]
pulseMonitors = ["US-WEST-1", "EU-CENTRAL-1"]
dependencies = ["db-primary", "redis-cache"]

[monitors.pulse.http]
method = "GET"
url = "https://api.example.com/health"
timeout = 10

[[monitors]]
id = "game-server"
name = "Game Server"
token = "tk_game_server"
interval = 10
maxRetries = 0
resendNotification = 0

[monitors.custom1]
id = "players"
name = "Player Count"
unit = "players"

[monitors.custom2]
id = "tps"
name = "Ticks Per Second"
unit = "TPS"

[monitors.custom3]
id = "memory"
name = "Memory Usage"
unit = "MB"

[[monitors]]
id = "db-primary"
name = "Primary Database"
token = "tk_db_primary"
interval = 15
maxRetries = 1
resendNotification = 6
notificationChannels = ["critical"]
pulseMonitors = ["US-WEST-1"]

[monitors.pulse.postgresql]
url = "postgresql://monitor:pass@db.example.com:5432/production"
timeout = 5

[[monitors]]
id = "redis-cache"
name = "Redis Cache"
token = "tk_redis"
interval = 10
maxRetries = 0
resendNotification = 0
pulseMonitors = ["US-WEST-1"]

[monitors.pulse.redis]
url = "redis://redis.example.com:6379"
timeout = 3

# Groups
[[groups]]
id = "production"
name = "Production Services"
strategy = "percentage"
degradedThreshold = 50
interval = 60
resendNotification = 12
children = ["api-prod", "game-server"]
notificationChannels = ["critical"]
dependencies = ["infrastructure"]

[[groups]]
id = "infrastructure"
name = "Infrastructure"
strategy = "all-up"
degradedThreshold = 0
interval = 60
resendNotification = 6
children = ["db-primary", "redis-cache"]
notificationChannels = ["critical", "ops-team"]

# Status Pages
[[status_pages]]
id = "public"
name = "Public Status"
slug = "status"
items = ["production", "infrastructure"]

[[status_pages]]
id = "internal"
name = "Internal Dashboard"
slug = "internal"
items = ["production", "infrastructure"]
password = "internal-secret-123"

# Notifications
[notifications.channels.critical]
id = "critical"
name = "Critical Alerts"
description = "High-priority production alerts"
enabled = true

[notifications.channels.critical.discord]
enabled = true
webhookUrl = "https://discord.com/api/webhooks/123456789/abcdefgh"
username = "Uptime Bot"

[notifications.channels.critical.email]
enabled = true
from = '"Uptime Monitor" <alerts@example.com>'
to = ["admin@example.com", "oncall@example.com"]

[notifications.channels.critical.email.smtp]
host = "smtp.gmail.com"
port = 465
secure = true
user = "alerts@example.com"
pass = "your-app-password"

[notifications.channels.ops-team]
id = "ops-team"
name = "Ops Team"
enabled = true

[notifications.channels.ops-team.ntfy]
enabled = true
server = "https://ntfy.sh"
topic = "my-uptime-alerts"

[notifications.channels.ops-team.telegram]
enabled = true
botToken = "123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
chatId = "-1001234567890"

[notifications.channels.ops-team.webhook]
enabled = true
url = "https://example.com/alerts/webhook"

[notifications.channels.ops-team.webhook.headers]
Authorization = "Bearer your-secret-token"
`;
	parseTOML(example);
	showToast("Example configuration loaded!");
}

function bindCollapsibles(container) {
	container.querySelectorAll(".collapsible-trigger").forEach((trigger) => {
		trigger.addEventListener("click", () => {
			trigger.classList.toggle("open");
			trigger.nextElementSibling.classList.toggle("open");
		});
	});
}

function handleBind(el) {
	const path = el.dataset.bind;
	if (!path) return;

	if (el.dataset.ensure) {
		ensurePath(el.dataset.ensure);
	}

	const { parent, key } = resolvePath(path);
	const dataType = el.dataset.type;
	const emptyUndefined = el.hasAttribute("data-empty-undefined");

	let value;
	if (dataType === "boolean") {
		value = el.checked;
	} else if (dataType === "number") {
		value = el.value === "" ? undefined : Number(el.value);
	} else if (dataType === "csv") {
		value = el.value
			.split(",")
			.map((s) => s.trim())
			.filter(Boolean);
	} else {
		value = el.value;
	}

	if (emptyUndefined && (value === "" || value === undefined)) {
		delete parent[key];
	} else {
		parent[key] = value;
	}

	if (el.hasAttribute("data-update-title")) {
		updateCardTitle(el);
	}

	if (el.dataset.rerender) {
		reRenderSection(el.dataset.rerender);
	}
}

function bindDynamicEvents(container) {
	// data-bind: change events for inputs, selects, checkboxes
	container.querySelectorAll("[data-bind]").forEach((el) => {
		el.addEventListener("change", () => handleBind(el));
	});

	// Multi-select dropdown: add value on selection
	container.querySelectorAll('[data-action="add-from-select"]').forEach((el) => {
		el.addEventListener("change", () => {
			const val = el.value;
			if (!val) return;
			addTag(el.dataset.section, Number(el.dataset.idx), el.dataset.prop, val);
		});
	});

	// Remove tag buttons
	container.querySelectorAll('[data-action="remove-tag"]').forEach((el) => {
		el.addEventListener("click", () => {
			removeTag(el.dataset.section, Number(el.dataset.idx), el.dataset.prop, el.dataset.value);
		});
	});

	// Pulse protocol selector
	container.querySelectorAll('[data-action="set-pulse-protocol"]').forEach((el) => {
		el.addEventListener("change", () => {
			setPulseProtocol(Number(el.dataset.idx), el.value);
		});
	});

	// Bind SNMP OID key/value change events
	bindSnmpOidEvents(container);

	// Webhook headers textarea: parse "Key: Value" lines into an object
	container.querySelectorAll('[data-action="webhook-headers"]').forEach((el) => {
		el.addEventListener("change", () => {
			const key = el.dataset.key;
			ensurePath(`notifications.channels.${key}.webhook`);
			const headers = {};
			el.value
				.split("\n")
				.map((l) => l.trim())
				.filter(Boolean)
				.forEach((line) => {
					const sep = line.indexOf(":");
					if (sep > 0) {
						headers[line.slice(0, sep).trim()] = line.slice(sep + 1).trim();
					}
				});
			if (Object.keys(headers).length > 0) {
				config.notifications.channels[key].webhook.headers = headers;
			} else {
				delete config.notifications.channels[key].webhook.headers;
			}
		});
	});

	// Update notification channel ID (and keep references in sync)
	container.querySelectorAll('[data-action="update-channel-id"]').forEach((el) => {
		el.addEventListener("change", () => {
			const ch = config.notifications.channels[el.dataset.key];
			if (!ch) return;
			const oldId = ch.id || el.dataset.key;
			const newId = el.value;
			ch.id = newId;
			replaceNotificationChannelRefs(oldId, newId);

			// Refresh dependent UIs (preserve edits first)
			const ml = document.getElementById("monitors-list");
			const gl = document.getElementById("groups-list");
			if (ml) flushBindings(ml);
			if (gl) flushBindings(gl);
			renderMonitors();
			renderGroups();
			updateBadges();
		});
	});
}

document.body.addEventListener("click", (e) => {
	const btn = e.target.closest("[data-action]");
	if (!btn) return;
	// Prevent accidental form submissions / navigation when buttons live inside other clickable areas
	e.preventDefault();
	e.stopPropagation();

	const action = btn.dataset.action;
	const idx = btn.dataset.idx !== undefined ? Number(btn.dataset.idx) : undefined;

	switch (action) {
		case "import-toml":
			document.getElementById("fileInput").click();
			break;
		case "load-example":
			loadExample();
			break;
		case "reset-config":
			resetConfig();
			break;
		case "export-toml":
			exportConfig();
			break;
		case "add-monitor":
			addMonitor();
			break;
		case "add-group":
			addGroup();
			break;
		case "add-statuspage":
			addStatusPage();
			break;
		case "add-notification":
			addNotificationChannel();
			break;
		case "add-pulsemonitor":
			addPulseMonitor();
			break;
		case "remove-monitor":
			removeMonitor(idx);
			break;
		case "remove-group":
			removeGroup(idx);
			break;
		case "remove-statuspage":
			removeStatusPage(idx);
			break;
		case "remove-notification":
			removeNotificationChannel(btn.dataset.key);
			break;
		case "remove-pulsemonitor":
			removePulseMonitor(idx);
			break;
		case "graph-fit":
			if (cyInstance) cyInstance.fit(48);
			break;
		case "graph-refresh":
			flushActiveTabEdits();
			updateBadges();
			break;
		case "graph-reset":
			renderGraph();
			break;
		case "open-server-sync":
			openServerSyncModal();
			break;
		case "close-server-sync":
			closeServerSyncModal();
			break;
		case "pull-from-server":
			pullFromServer();
			break;
		case "push-to-server":
			pushToServer();
			break;
		case "toggle-token-visibility":
			toggleTokenVisibility();
			break;
		case "add-snmp-oid":
			addSnmpOid(idx);
			break;
		case "remove-snmp-oid":
			removeSnmpOid(idx, Number(btn.dataset.oidIdx));
			break;
		case "set-snmp-version":
			handleBind(btn);
			reRenderSnmpFields(idx);
			break;
	}
});

let cyInstance = null;

const NODE_COLORS = {
	monitor: { bg: "#10b981", border: "#059669", glow: "rgba(16,185,129,0.35)" },
	group: { bg: "#3b82f6", border: "#2563eb", glow: "rgba(59,130,246,0.35)" },
	statuspage: { bg: "#8b5cf6", border: "#7c3aed", glow: "rgba(139,92,246,0.35)" },
	notification: { bg: "#f59e0b", border: "#d97706", glow: "rgba(245,158,11,0.35)" },
	pulse: { bg: "#06b6d4", border: "#0891b2", glow: "rgba(6,182,212,0.35)" },
};

const EDGE_COLORS = {
	"parent-child": "#3b82f6",
	"monitor-notification": "#f59e0b",
	"monitor-pulse": "#06b6d4",
	"group-notification": "#f59e0b",
	"statuspage-item": "#8b5cf6",
	dependency: "#ef4444",
};

function updateGraphFilterOptions() {
	const select = document.getElementById("graph-filter");
	const currentVal = select.value;

	// Keep "All" as first option
	let optionsHtml = `<option value="all">Show All Connections</option>`;

	config.status_pages.forEach((sp) => {
		if (sp.id) {
			const label = sp.name || sp.slug || sp.id;
			optionsHtml += `<option value="sp-${sp.id}">${esc(label)}</option>`;
		}
	});

	select.innerHTML = optionsHtml;

	// Restore selection if it still exists, otherwise default to all
	if (currentVal && select.querySelector(`option[value="${currentVal}"]`)) {
		select.value = currentVal;
	} else {
		select.value = "all";
	}
}

function buildGraphData() {
	const nodes = [];
	const edges = [];
	const nodeIds = new Set();

	// 1. Monitors
	for (const m of config.monitors) {
		if (!m.id) continue;
		const nid = `mon-${m.id}`;
		nodes.push({ data: { id: nid, label: m.name || m.id, type: "monitor" } });
		nodeIds.add(nid);

		if (m.children) {
			for (const childId of m.children) {
				const childMonTarget = `mon-${childId}`;
				const childGrpTarget = `grp-${childId}`;
				if (config.monitors.some((x) => x.id === childId)) {
					edges.push({ data: { source: nid, target: childMonTarget, edgeType: "parent-child" } });
				} else {
					edges.push({ data: { source: nid, target: childGrpTarget, edgeType: "parent-child" } });
				}
			}
		}
		if (m.notificationChannels) {
			for (const nc of m.notificationChannels) {
				edges.push({ data: { source: nid, target: `ntf-${nc}`, edgeType: "monitor-notification" } });
			}
		}
		if (m.pulseMonitors) {
			for (const pm of m.pulseMonitors) {
				edges.push({ data: { source: nid, target: `pm-${pm}`, edgeType: "monitor-pulse" } });
			}
		}
		if (m.dependencies) {
			for (const dep of m.dependencies) {
				const depMonTarget = `mon-${dep}`;
				const depGrpTarget = `grp-${dep}`;
				if (config.monitors.some((x) => x.id === dep)) {
					edges.push({ data: { source: nid, target: depMonTarget, edgeType: "dependency" } });
				} else {
					edges.push({ data: { source: nid, target: depGrpTarget, edgeType: "dependency" } });
				}
			}
		}
	}

	// 2. Groups
	for (const g of config.groups) {
		if (!g.id) continue;
		const nid = `grp-${g.id}`;
		if (!nodeIds.has(nid)) {
			nodes.push({ data: { id: nid, label: g.name || g.id, type: "group" } });
			nodeIds.add(nid);
		}
		if (g.children) {
			for (const childId of g.children) {
				const childMonTarget = `mon-${childId}`;
				const childGrpTarget = `grp-${childId}`;
				if (config.monitors.some((x) => x.id === childId)) {
					edges.push({ data: { source: nid, target: childMonTarget, edgeType: "parent-child" } });
				} else {
					edges.push({ data: { source: nid, target: childGrpTarget, edgeType: "parent-child" } });
				}
			}
		}
		if (g.notificationChannels) {
			for (const nc of g.notificationChannels) {
				edges.push({ data: { source: nid, target: `ntf-${nc}`, edgeType: "group-notification" } });
			}
		}
		if (g.dependencies) {
			for (const dep of g.dependencies) {
				const depMonTarget = `mon-${dep}`;
				const depGrpTarget = `grp-${dep}`;
				if (config.monitors.some((x) => x.id === dep)) {
					edges.push({ data: { source: nid, target: depMonTarget, edgeType: "dependency" } });
				} else {
					edges.push({ data: { source: nid, target: depGrpTarget, edgeType: "dependency" } });
				}
			}
		}
	}

	// 3. Status Pages
	for (const sp of config.status_pages) {
		if (!sp.id) continue;
		const nid = `sp-${sp.id}`;
		nodes.push({ data: { id: nid, label: sp.name || sp.slug || sp.id, type: "statuspage" } });
		nodeIds.add(nid);

		if (sp.items) {
			for (const item of sp.items) {
				const monTarget = `mon-${item}`;
				const grpTarget = `grp-${item}`;
				// Status Page -> Item (Monitor or Group)
				if (nodeIds.has(monTarget)) {
					edges.push({ data: { source: nid, target: monTarget, edgeType: "statuspage-item" } });
				} else {
					edges.push({ data: { source: nid, target: grpTarget, edgeType: "statuspage-item" } });
				}
			}
		}
	}

	// 4. Notifications (Leaf nodes)
	const channels = config.notifications?.channels || {};
	for (const key of Object.keys(channels)) {
		const ch = channels[key];
		const chId = ch.id || key;
		const nid = `ntf-${chId}`;
		if (!nodeIds.has(nid)) {
			nodes.push({ data: { id: nid, label: ch.name || chId, type: "notification" } });
			nodeIds.add(nid);
		}
	}

	// 5. PulseMonitors (Leaf nodes)
	for (const pm of config.PulseMonitors) {
		if (!pm.id) continue;
		const nid = `pm-${pm.id}`;
		if (!nodeIds.has(nid)) {
			nodes.push({ data: { id: nid, label: pm.name || pm.id, type: "pulse" } });
			nodeIds.add(nid);
		}
	}

	// Only return edges where both nodes actually exist
	const validEdges = edges.filter((e) => nodeIds.has(e.data.source) && nodeIds.has(e.data.target));

	return { nodes, edges: validEdges };
}

function renderGraph() {
	// 1. Build the full map of everything
	let { nodes, edges } = buildGraphData();

	const filterSelect = document.getElementById("graph-filter");
	const filterId = filterSelect ? filterSelect.value : "all";

	// 2. Apply Filtering if not "all"
	if (filterId !== "all" && filterId.startsWith("sp-")) {
		const keepNodeIds = new Set();
		const queue = [filterId];

		// Add the Status Page itself
		keepNodeIds.add(filterId);

		// Breadth-First Search to find all descendants
		// (StatusPage -> Group -> Monitor -> Notification/Pulse)
		while (queue.length > 0) {
			const currentId = queue.shift();

			// Find all edges starting from this node
			const outgoingEdges = edges.filter((e) => e.data.source === currentId);

			for (const edge of outgoingEdges) {
				const targetId = edge.data.target;
				if (!keepNodeIds.has(targetId)) {
					keepNodeIds.add(targetId);
					queue.push(targetId);
				}
			}
		}

		// Filter the arrays
		nodes = nodes.filter((n) => keepNodeIds.has(n.data.id));
		edges = edges.filter((e) => keepNodeIds.has(e.data.source) && keepNodeIds.has(e.data.target));
	}

	const emptyEl = document.getElementById("graph-empty");
	const container = document.getElementById("cy-graph");

	if (nodes.length === 0) {
		emptyEl.classList.remove("hidden");
		if (cyInstance) {
			cyInstance.destroy();
			cyInstance = null;
		}
		return;
	}

	emptyEl.classList.add("hidden");

	if (cyInstance) cyInstance.destroy();

	cyInstance = cytoscape({
		container: container,
		elements: [...nodes, ...edges],
		minZoom: 0.3,
		maxZoom: 3,
		wheelSensitivity: 0.3,
		style: [
			{
				selector: "node",
				style: {
					label: "data(label)",
					"text-valign": "bottom",
					"text-halign": "center",
					"text-margin-y": 8,
					"font-size": 11,
					"font-family": "system-ui, -apple-system, sans-serif",
					"font-weight": 500,
					color: "#9ca3af",
					"text-outline-width": 2,
					"text-outline-color": "#050810",
					"text-outline-opacity": 0.9,
					width: 40,
					height: 40,
					"border-width": 2,
					"overlay-padding": 6,
					"overlay-opacity": 0,
				},
			},
			...Object.entries(NODE_COLORS).map(([type, colors]) => ({
				selector: `node[type="${type}"]`,
				style: {
					"background-color": colors.bg,
					"border-color": colors.border,
					"background-opacity": 0.9,
				},
			})),
			{
				selector: "edge",
				style: {
					width: 1.5,
					"line-color": "#374151",
					"target-arrow-color": "#374151",
					"target-arrow-shape": "triangle",
					"arrow-scale": 0.8,
					"curve-style": "bezier",
					opacity: 0.5,
				},
			},
			{
				selector: 'edge[edgeType="parent-child"]',
				style: {
					"line-style": "dashed",
					"target-arrow-shape": "triangle",
				},
			},
			{
				selector: 'edge[edgeType="dependency"]',
				style: {
					"line-style": "dashed",
					"target-arrow-shape": "triangle",
				},
			},
			...Object.entries(EDGE_COLORS).map(([type, color]) => ({
				selector: `edge[edgeType="${type}"]`,
				style: {
					"line-color": color,
					"target-arrow-color": color,
				},
			})),
		],
		layout: {
			name: "dagre",
			rankDir: "TB",
			rankSep: 80,
			nodeSep: 60,
			padding: 48,
			animate: true,
			animationDuration: 500,
		},
	});

	// Hover effects
	cyInstance.on("mouseover", "node", (e) => {
		const node = e.target;
		node.style({
			width: 48,
			height: 48,
			"border-width": 3,
			"border-color": "#ffffff",
			"font-size": 12,
			color: "#ffffff",
			"z-index": 999,
		});
		const connected = node.connectedEdges();
		connected.style({ opacity: 1, width: 3, "z-index": 998 });
		node.neighborhood("node").style({
			"border-width": 3,
			"border-color": "#fff",
			"z-index": 998,
		});
		cyInstance.elements().not(node).not(connected).not(node.neighborhood("node")).style({ opacity: 0.1 });
	});

	cyInstance.on("mouseout", "node", () => {
		cyInstance.elements().removeStyle();
	});
}

document.getElementById("graph-filter").addEventListener("change", () => {
	renderGraph();
});

loadConfigToUI();
